import { memo } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { MessageCitationMaybeLink } from './Citation.MaybeLink';
import { SnippetRoot } from './Citation.Snippet';

type Props = {
  metadata: Chat.SurveyCitationSnippetMetadata;
  baseUrl?: string;
  to?: string | null;
};

function formatOptionCount({ text, count, pct }: Chat.SurveyCitationOptionCount): string {
  return `${text} (${count} ${count === 1 ? 'respondent' : 'respondents'}, ${Math.round(pct)}%)`;
}

// One survey question: its option counts for a choice question, else `Respondent N: "answer"` lines.
export const SurveyRespondentsCitation = memo(({ metadata, baseUrl, to }: Props) => {
  const omitted = metadata.omittedRespondentCount ?? 0;
  const respondents = metadata.respondents ?? [];
  const distribution = [...(metadata.distribution ?? [])].sort((a, b) => b.count - a.count);

  return (
    <Panel>
      {distribution.length > 0 ? (
        distribution.map((option, i) => <Line key={i}>{formatOptionCount(option)}</Line>)
      ) : (
        <>
          {!respondents.length && <More>No answers to show for this question</More>}
          {respondents.map((respondent) => (
            <Line key={respondent.respondentOrder}>
              Respondent {respondent.respondentOrder}: &ldquo;{respondent.answer}&rdquo;
            </Line>
          ))}
          {omitted > 0 && <More>+{omitted} more</More>}
        </>
      )}
      {!!to && (
        <Footer>
          <MessageCitationMaybeLink to={to} baseUrl={baseUrl}>
            <View>{`View >`}</View>
          </MessageCitationMaybeLink>
        </Footer>
      )}
    </Panel>
  );
});

const Panel = styled(SnippetRoot)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const Line = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
}));

const Footer = styled.div({
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
});

const View = styled.div(({ theme }) => ({
  fontSize: 13,
  fontFamily: theme.fonts.semiBold,
}));

const More = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 12,
  color: theme.palette.gray.dark1,
}));
